import { Text } from "@chakra-ui/react";
import { LearnMore } from "src/components/Links/LearnMore";
import * as FSQ from "src/generated/hooks/form-service/queries";
import {
  deleteBinCriterion,
  deleteOfferingOptionCriterion,
  deleteSchoolCriterion,
  upsertBinCriterion,
  upsertOfferingOptionCriterion,
  upsertSchoolCriterion,
  type EligibilityConditions,
} from "src/generated/hooks/form-service/requests";
import {
  fetchBinCriterion,
  fetchOfferingOptionCriterion,
  fetchSchoolCriterion,
  type LoadedCriterion,
} from "./fetchCriterion";

export type InheritedCriterionSource = {
  id: string;
  label: string;
  queryKey: (criteriaSetId: string) => readonly unknown[];
  fetch: (criteriaSetId: string) => Promise<LoadedCriterion>;
};

export type OfferingOption = {
  id: string;
  label: string;
  categoryLabel: string;
};

export type Bin = {
  id: string;
  label: string;
  school: { id: string; name: string };
  offeringOptions: OfferingOption[];
  binOption: OfferingOption | null;
};

export type CriterionForEntity = {
  entityId: string;
  entityLabel: string;
  description: (criteriaSetName?: string) => React.ReactNode;
  queryKey: (criteriaSetId: string) => readonly unknown[];
  fetch: (criteriaSetId: string) => Promise<LoadedCriterion>;
  upsert: (
    criteriaSetId: string,
    body: {
      conditions: EligibilityConditions;
      inheritConditions?: boolean;
    }
  ) => Promise<void>;
  delete: (criteriaSetId: string) => Promise<void>;
  inheritedSources?: InheritedCriterionSource[];
};

export function criterionForOfferingOption({
  enrollmentPeriodId,
  option,
  useSeatTypes,
}: {
  enrollmentPeriodId: string;
  option: OfferingOption;
  useSeatTypes: boolean;
}): CriterionForEntity {
  const subjects = eligibilitySubjects(useSeatTypes);

  return {
    entityId: option.id,
    entityLabel: `${option.categoryLabel}: ${option.label}`,
    description: (criteriaSetName) => (
      <Text fontSize="sm">
        Define the conditions that all {subjects} with{" "}
        <b>
          {option.categoryLabel}: {option.label}
        </b>{" "}
        must meet to pass{" "}
        {criteriaSetName ? <b>{criteriaSetName}</b> : "each criteria set"}.
        {/* TODO: Add help article slug */} <LearnMore article="" />
      </Text>
    ),
    queryKey: (criteriaSetId) => [
      FSQ.useGetOfferingOptionCriterionKey,
      enrollmentPeriodId,
      criteriaSetId,
      option.id,
    ],
    fetch: (criteriaSetId) =>
      fetchOfferingOptionCriterion({
        enrollmentPeriodId,
        criteriaSetId,
        offeringOptionId: option.id,
      }),
    upsert: async (criteriaSetId, body) => {
      await upsertOfferingOptionCriterion({
        throwOnError: true,
        path: {
          enrollmentPeriodId,
          criteriaSetId,
          offeringOptionId: option.id,
        },
        body,
      });
    },
    delete: async (criteriaSetId) => {
      await deleteOfferingOptionCriterion({
        throwOnError: true,
        path: {
          enrollmentPeriodId,
          criteriaSetId,
          offeringOptionId: option.id,
        },
      });
    },
  };
}

export function criterionForSchool({
  enrollmentPeriodId,
  schoolId,
  schoolName,
  useSeatTypes,
}: {
  enrollmentPeriodId: string;
  schoolId: string;
  schoolName: string;
  useSeatTypes: boolean;
}): CriterionForEntity {
  const subjects = eligibilitySubjects(useSeatTypes);

  return {
    entityId: schoolId,
    entityLabel: schoolName,
    description: (criteriaSetName) => (
      <Text fontSize="sm">
        Define the conditions that all {subjects} at <b>{schoolName}</b> must
        meet to pass{" "}
        {criteriaSetName ? <b>{criteriaSetName}</b> : "each criteria set"}.
        {/* TODO: Add help article slug */} <LearnMore article="" />
      </Text>
    ),
    queryKey: (criteriaSetId) => [
      FSQ.useGetSchoolCriterionKey,
      enrollmentPeriodId,
      criteriaSetId,
      schoolId,
    ],
    fetch: (criteriaSetId) =>
      fetchSchoolCriterion({
        enrollmentPeriodId,
        criteriaSetId,
        schoolId,
      }),
    upsert: async (criteriaSetId, body) => {
      await upsertSchoolCriterion({
        throwOnError: true,
        path: { enrollmentPeriodId, criteriaSetId, schoolId },
        body,
      });
    },
    delete: async (criteriaSetId) => {
      await deleteSchoolCriterion({
        throwOnError: true,
        path: { enrollmentPeriodId, criteriaSetId, schoolId },
      });
    },
  };
}

export function criterionForBin({
  enrollmentPeriodId,
  binId,
  binLabel,
  school,
  offeringOptions,
  binOption,
}: {
  enrollmentPeriodId: string;
  binId: string;
  binLabel: string;
  school: { id: string; name: string };
  offeringOptions: OfferingOption[];
  binOption: OfferingOption | null;
}): CriterionForEntity {
  const inheritedSources = [
    inheritedSchoolSource(enrollmentPeriodId, school),
    ...offeringOptions.map((option) =>
      inheritedOptionSource(enrollmentPeriodId, option)
    ),
    ...(binOption
      ? [inheritedOptionSource(enrollmentPeriodId, binOption)]
      : []),
  ];

  return {
    entityId: binId,
    entityLabel: binLabel,
    description: (criteriaSetName) => (
      <Text fontSize="sm">
        Define the conditions that <b>{binLabel}</b> must meet to pass{" "}
        {criteriaSetName ? <b>{criteriaSetName}</b> : "each criteria set"}.
        {/* TODO: Add help article slug */} <LearnMore article="" />
      </Text>
    ),
    queryKey: (criteriaSetId) => [
      FSQ.useGetBinCriterionKey,
      enrollmentPeriodId,
      criteriaSetId,
      binId,
    ],
    fetch: (criteriaSetId) =>
      fetchBinCriterion({
        enrollmentPeriodId,
        criteriaSetId,
        binId,
      }),
    inheritedSources,
    upsert: async (criteriaSetId, body) => {
      await upsertBinCriterion({
        throwOnError: true,
        path: { enrollmentPeriodId, criteriaSetId, binId },
        body: {
          conditions: body.conditions,
          inheritConditions: body.inheritConditions ?? true,
        },
      });
    },
    delete: async (criteriaSetId) => {
      await deleteBinCriterion({
        throwOnError: true,
        path: { enrollmentPeriodId, criteriaSetId, binId },
      });
    },
  };
}

function eligibilitySubjects(useSeatTypes: boolean) {
  return useSeatTypes ? "offering seat types" : "offerings";
}

function inheritedSchoolSource(
  enrollmentPeriodId: string,
  school: { id: string; name: string }
): InheritedCriterionSource {
  return {
    id: `school:${school.id}`,
    label: school.name,
    queryKey: (criteriaSetId) => [
      FSQ.useGetSchoolCriterionKey,
      enrollmentPeriodId,
      criteriaSetId,
      school.id,
    ],
    fetch: (criteriaSetId) =>
      fetchSchoolCriterion({
        enrollmentPeriodId,
        criteriaSetId,
        schoolId: school.id,
      }),
  };
}

function inheritedOptionSource(
  enrollmentPeriodId: string,
  option: OfferingOption
): InheritedCriterionSource {
  return {
    id: `offering-option:${option.id}`,
    label: `${option.categoryLabel}: ${option.label}`,
    queryKey: (criteriaSetId) => [
      FSQ.useGetOfferingOptionCriterionKey,
      enrollmentPeriodId,
      criteriaSetId,
      option.id,
    ],
    fetch: (criteriaSetId) =>
      fetchOfferingOptionCriterion({
        enrollmentPeriodId,
        criteriaSetId,
        offeringOptionId: option.id,
      }),
  };
}
